// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullRegattaAdminRequest,
} from 'common.data';

// Global Enums
import {
  RegattaAdminRequestStatus,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  CardBody,
  Table,
} from 'reactstrap';
import {
  CommonDialog,
  DeleteDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../../helpers';

// Components
import ThQty from '../../../common/ThQty';

// Services
import Services from '../../../../services';

// Types
import type {
  $ReducerState,
} from '../../../../redux';

export type $OwnProps = $Translate & {
  id: string;
};

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$FullRegattaAdminRequest>;
  loading: boolean;
};

class RegattaAdminRequestsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      main,
    } = this.props;

    if (isDiff(
      main.activeRegionSeasonId,
      prevProps.main.activeRegionSeasonId,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          main,
        } = this.props;

        if (main.activeRegionSeasonId === data.RegionSeasonId
          && data.modelName === 'RegattaAdminRequest'
        ) {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegionSeasonId = main.activeRegionSeasonId;

    await defaultGetData({
      action: async () => {
        if (RegionSeasonId !== null) {
          await this.setStateAsync({
            loading: true,
          });

          const data = await Services.Api.getManyRegattaAdminRequest({
            RegionSeasonId,
          });

          await this.setStateAsync({
            data,
            loading: false,
          });
        }
      },
      errorAction: async () => this.stopLoader(),
    });
  }

  async stopLoader(): Promise<void> {
    await this.setStateAsync({
      loading: false,
    });
  }

  renderButtons(dataItem: $FullRegattaAdminRequest) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <CommonDialog
          content={(
            <CardBody>
              {t('Do you really want to add this user to admins?')}
            </CardBody>
          )}
          id={`confirm-regatta-admin-request-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelConfirmButton={t('Confirm')}
          onAction={() => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$FullRegattaAdminRequest>({
                ...dataItem,
                Status: RegattaAdminRequestStatus.confirmed,
              });

              await Services.Api.upsertRegattaAdminRequest(params);
            },
          })}
          title={t('Need confirmation')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`confirm-regatta-admin-request-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Confirm')}
          </Button>
        </CommonDialog>
        <DeleteDialog
          content={t('Do you really want to reject?')}
          id={`reject-regatta-admin-request-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.rejectRegattaAdminRequest(dataItem),
          })}
          subTitle={''}
          title={t('Regatta admin request')}
        >
          <Button
            color={'danger'}
            id={`reject-regatta-admin-request-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Reject')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderData() {
    const {
      data,
    } = this.state;

    return (
      <>
        {data.map((dataItem: $FullRegattaAdminRequest) => (
          <tr key={dataItem.id}>
            <TdDate
              duration
              multiple
              value={dataItem.createdAt}
            />
            <td>{dataItem.Regatta.Name}</td>
            <td>{dataItem.Name}</td>
            <td>{dataItem.Email}</td>
            <td>
              {this.renderButtons(dataItem)}
            </td>
          </tr>
        ))}
      </>
    );
  }

  renderHeader() {
    const {
      t,
    } = this.props;

    return (
      <SectionHeader
        bottomBorder
        iconClass={'icon-user'}
        title={t('Pending regatta admin requests')}
      />
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Date')}</th>
            <th>{t('Regatta')}</th>
            <th>{t('Name')}</th>
            <th>{t('Email')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {this.renderData()}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Loader
        loading={loading === true}
        overlay
      >
        {this.renderTable()}
      </Loader>
    );
  }

  render() {
    const {
      id,
    } = this.props;
    const {
      data,
    } = this.state;

    return (data.length > 0 ? (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    ) : null);
  }
}

export default RegattaAdminRequestsSectionView;
